import { toast } from "sonner";
import { Receipt, X } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { api, errDetail, fmtDate, fmtDateTime, money } from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export function StatusBadge({ s }) {
  const { t } = useLang();
  const c = { active: "border-emerald-500/40 text-emerald-300", confirmed: "border-emerald-500/40 text-emerald-300", completed: "border-slate-500/40 text-slate-400", cancelled: "border-red-500/40 text-red-300", canceled: "border-red-500/40 text-red-300", past_due: "border-amber-500/40 text-amber-300", paused: "border-amber-500/40 text-amber-300", waiting: "border-sky-500/40 text-sky-300", notified: "border-emerald-500/40 text-emerald-300" }[s] || "border-white/15 text-slate-400";
  return <span className={`inline-block rounded-full border px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-wide ${c}`}>{t(`status.${s}`)}</span>;
}

function CancelBtn({ b, deadlineH, onDone }) {
  const { t, tErr } = useLang();
  const late = new Date(b.start).getTime() - deadlineH * 3600000 < Date.now();
  const go = async () => {
    try {
      const { data } = await api.post(`/member/bookings/${b.id}/cancel`);
      toast.success(`${t("member.cancelled")}${data.refunded ? ` · +${b.hours_deducted} h` : ""}`);
      onDone();
    } catch (e) { toast.error(tErr(errDetail(e))); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`cancel-booking-btn-${b.id}`} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-300 hover:border-red-500 hover:text-red-300">{t("member.cancel")}</button>
      </AlertDialogTrigger>
      <AlertDialogContent className="border-white/10 bg-[#0f1015]">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("member.cancelConfirm")}</AlertDialogTitle>
          <AlertDialogDescription className={late ? "text-amber-300" : "text-slate-400"} data-testid="cancel-policy-msg">{late ? t("member.cancelLate") : t("member.cancelOk")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cancel-dialog-dismiss">{t("common.close")}</AlertDialogCancel>
          <AlertDialogAction onClick={go} className="bg-red-600 hover:bg-red-700" data-testid="cancel-dialog-confirm">{t("member.cancel")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function BookingList({ items, empty, deadlineH, onChange, cancellable, testId }) {
  const { lang } = useLang();
  if (!items.length) return <p className="panel p-6 text-sm text-slate-400" data-testid={`${testId}-empty`}>{empty}</p>;
  return (
    <ul className="space-y-2" data-testid={testId}>
      {items.map((b) => (
        <li key={b.id} className="panel flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`booking-row-${b.id}`}>
          <div>
            <p className="capitalize text-white">{fmtDate(b.local_date, lang)}</p>
            <p className="font-mono text-sm text-slate-400">{b.local_start}–{b.local_end} · {b.duration_hours} h</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge s={b.status} />
            {cancellable && b.status === "confirmed" && <CancelBtn b={b} deadlineH={deadlineH} onDone={onChange} />}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function WaitlistList({ items, onChange }) {
  const { t, lang } = useLang();
  if (!items.length) return null;
  const leave = async (id) => { await api.delete(`/member/waitlist/${id}`); onChange(); };
  return (
    <div className="mt-8" data-testid="member-waitlist">
      <p className="eyebrow mb-3">{t("member.waitlist")}</p>
      <ul className="space-y-2">
        {items.map((w) => (
          <li key={w.id} className="panel flex items-center justify-between gap-3 p-4">
            <span className="text-sm text-slate-200"><span className="capitalize">{fmtDate(w.local_date, lang)}</span> · <span className="font-mono">{w.local_start}–{w.local_end}</span></span>
            <span className="flex items-center gap-2"><StatusBadge s={w.status} />
              <button onClick={() => leave(w.id)} data-testid={`waitlist-leave-${w.id}`} className="text-slate-500 hover:text-red-400" aria-label={t("member.leave")}><X size={16} /></button></span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PaymentsList({ items }) {
  const { t, lang } = useLang();
  if (!items.length) return <p className="panel p-6 text-sm text-slate-400" data-testid="payments-empty">{t("member.paymentsEmpty")}</p>;
  return (
    <ul className="space-y-2" data-testid="payments-list">
      {items.map((p) => (
        <li key={p.id} className="panel flex flex-wrap items-center justify-between gap-3 p-4">
          <div><p className="text-white">{money(p.amount, lang)}</p><p className="text-xs text-slate-500">{fmtDateTime(p.created_at, lang)} · {p.number || ""}</p></div>
          {p.receipt_url && <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" className="btn-chrome inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs" data-testid={`receipt-link-${p.id}`}><Receipt size={14} />{t("member.receipt")}</a>}
        </li>
      ))}
    </ul>
  );
}

export function NotificationsList({ items }) {
  const { t, lang } = useLang();
  if (!items.length) return <p className="panel p-6 text-sm text-slate-400">{t("member.alertsEmpty")}</p>;
  return (
    <ul className="space-y-2" data-testid="notifications-list">
      {items.map((n) => (
        <li key={n.id} className="panel p-4">
          <p className="font-medium text-white">{n.title}</p>
          <p className="text-sm text-slate-400">{n.message}</p>
          <p className="mt-1 font-mono text-[11px] text-slate-600">{fmtDateTime(n.created_at, lang)}</p>
        </li>
      ))}
    </ul>
  );
}
